import { Component, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { MessagesModule } from 'primeng/messages';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { CargaCumplimientoRepository } from 'src/app/domain/service/cumplimiento/carga-cumplimiento-repository';
import {
  CargaCumplimiento,
  EstadoCarga
} from 'src/app/domain/model/cumplimiento/carga-cumplimiento';
import { environment } from 'src/environments/environment';
import { massiveResultPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * Componente: ResultadoCargaMasivaComponent
 *
 * Formulario de consulta de resultado de carga masiva de cumplimientos Sin OC.
 *
 * Replica exactamente el legacy GWT:
 * - CumplimientoSinOcViewImpl.java    -> formulario de filtros y validaciones
 * - CumplimientoSinOcActivity.java    -> generadorReportesExcel()
 * - AdminCumplimientoHandler.java     -> consultaCargaCumplimiento()
 *
 * Validaciones (del legacy - validarCampos()):
 * 1. Fecha inicial no puede ser nula
 * 2. Fecha final no puede ser nula
 * 3. Al menos un estado seleccionado (PENDIENTE, PROCESADO o ERROR)
 * 4. Fecha inicial <= Fecha final
 *
 * @author Sura - Migracion GWT a Angular
 */
@Component({
  selector: 'app-resultado-carga-masiva',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    DatePickerModule,
    CheckboxModule,
    TableModule,
    ToastModule,
    MessagesModule,
    ProgressSpinnerModule,
    TagModule,
    TooltipModule,
    DatePipe,
    BusquedaPaginadorComponent
  ],
  providers: [MessageService],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <div class="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto overflow-x-hidden">
      <p-toast />

      <!-- Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
          Resultado Carga Masiva
        </h1>
        <p class="text-gray-600 dark:text-gray-400 text-sm">
          Consulta de registros procesados en carga masiva de cumplimientos Sin
          OC
        </p>
      </div>

      <!-- Filtros -->
      <p-card styleClass="mb-4">
        <ng-template pTemplate="header">
          <div class="px-4 pt-4 font-semibold text-gray-700 dark:text-gray-300">
            Filtros de Consulta
          </div>
        </ng-template>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <!-- Fecha inicial -->
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">
              Fecha inicial <span class="text-red-500">*</span>
            </label>
            <p-datepicker
              [(ngModel)]="fechaInicial"
              [showIcon]="true"
              dateFormat="dd/mm/yy"
              placeholder="dd/mm/aaaa"
              styleClass="w-full"
              [maxDate]="fechaFinal ?? today"
              [showButtonBar]="true"
              [monthNavigator]="true"
              [yearNavigator]="true"
              [yearRange]="'2000:2030'"
              appendTo="body"
              [showTransitionOptions]="'0ms'"
              [hideTransitionOptions]="'0ms'"
              [panelStyle]="{ minWidth: '18rem', zIndex: 1300 }"
            />
          </div>

          <!-- Fecha final -->
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">
              Fecha final <span class="text-red-500">*</span>
            </label>
            <p-datepicker
              [(ngModel)]="fechaFinal"
              [showIcon]="true"
              dateFormat="dd/mm/yy"
              placeholder="dd/mm/aaaa"
              styleClass="w-full"
              [minDate]="fechaInicial ?? undefined"
              [showButtonBar]="true"
              [monthNavigator]="true"
              [yearNavigator]="true"
              [yearRange]="'2000:2030'"
              appendTo="body"
              [showTransitionOptions]="'0ms'"
              [hideTransitionOptions]="'0ms'"
              [panelStyle]="{ minWidth: '18rem', zIndex: 1300 }"
            />
          </div>
        </div>

        <!-- Estados -->
        <div class="mb-4">
          <label class="block text-sm text-gray-600 dark:text-gray-300 mb-2">
            Estado <span class="text-red-500">*</span>
          </label>
          <div class="flex flex-wrap gap-4">
            <div class="flex items-center gap-2">
              <p-checkbox
                [(ngModel)]="pendiente"
                [binary]="true"
                inputId="chkPendiente"
              />
              <label for="chkPendiente" class="cursor-pointer">Pendiente</label>
            </div>
            <div class="flex items-center gap-2">
              <p-checkbox
                [(ngModel)]="procesado"
                [binary]="true"
                inputId="chkProcesado"
              />
              <label for="chkProcesado" class="cursor-pointer">Procesado</label>
            </div>
            <div class="flex items-center gap-2">
              <p-checkbox
                [(ngModel)]="error"
                [binary]="true"
                inputId="chkError"
              />
              <label for="chkError" class="cursor-pointer">Error</label>
            </div>
          </div>
        </div>

        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="limpiar()"
          />
          <p-button
            label="Exportar"
            icon="pi pi-download"
            [loading]="cargando()"
            styleClass="min-h-[44px]"
            (onClick)="exportarExcel()"
          />
        </div>
      </p-card>

      <!-- Cargando -->
      @if (cargando()) {
        <div class="flex justify-center py-8">
          <p-progressSpinner strokeWidth="4" />
        </div>
      }

      <!-- Tabla de resultados -->
      @if (!cargando() && resultados().length > 0) {
        <p-card>
          <ng-template pTemplate="header">
            <div
              class="px-4 pt-4 flex flex-wrap items-center justify-between gap-2"
            >
              <span class="font-semibold text-gray-700 dark:text-gray-300">
                Resultados ({{ resultados().length }} registros)
              </span>
              @if (totalRegistros() > 0) {
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de {{ totalRegistros() }}
                </span>
              }
            </div>
          </ng-template>

          <div class="md:hidden space-y-3 p-3">
            @for (reg of paginatedResultados(); track reg.nmconsecutivo) {
              <article
                class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
              >
                <div class="flex items-start justify-between gap-2 mb-2">
                  <h3
                    class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                  >
                    Registro {{ reg.nmconsecutivo }}
                  </h3>
                  <p-tag
                    [value]="reg.cdestado"
                    [severity]="getSeveridad(reg.cdestado)"
                  ></p-tag>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >DNI Beneficiario:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      reg.dsdniAseguradora ?? '-'
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Forma Entrega:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      reg.cdformaEntrega ?? '-'
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Fecha Programacion:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      reg.feprogramacion | date: 'dd/MM/yyyy'
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Fecha Ingreso:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      reg.feingresa | date: 'dd/MM/yyyy'
                    }}</span>
                  </div>
                  <div class="sm:col-span-2">
                    <span class="text-gray-500 dark:text-gray-400"
                      >Descripcion Error:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      reg.dserrorInsert ?? '-'
                    }}</span>
                  </div>
                </div>
              </article>
            }
          </div>

          <div class="hidden md:block">
            <div
              role="region"
              [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
              class="sr-only"
            ></div>
            <p-table
              [resizableColumns]="true"
              columnResizeMode="expand"
              [value]="paginatedResultados()"
              [paginator]="false"
              [rows]="pageSize()"
              [scrollable]="true"
              [responsiveLayout]="'scroll'"
              [scrollHeight]="tableConfig().responsive.scrollHeight || '320px'"
              [styleClass]="tableStyleClass()"
              [tableStyle]="{
                'min-width': tableConfig().responsive.minWidth || '72rem'
              }"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="ID">ID</span>
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="DNI Beneficiario"
                      >DNI Beneficiario</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Forma Entrega"
                      >Forma Entrega</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Fecha Programacion"
                      >Fecha Programacion</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Fecha Ingreso"
                      >Fecha Ingreso</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Estado">Estado</span>
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Descripcion Error"
                      >Descripcion Error</span
                    >
                  </th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-reg>
                <tr>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(reg.nmconsecutivo)"
                      >{{ textoTabla(reg.nmconsecutivo) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(reg.dsdniAseguradora)"
                      >{{ textoTabla(reg.dsdniAseguradora) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(reg.cdformaEntrega)"
                      >{{ textoTabla(reg.cdformaEntrega) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        textoTabla(reg.feprogramacion | date: 'dd/MM/yyyy')
                      "
                      >{{
                        textoTabla(reg.feprogramacion | date: 'dd/MM/yyyy')
                      }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        textoTabla(reg.feingresa | date: 'dd/MM/yyyy')
                      "
                      >{{
                        textoTabla(reg.feingresa | date: 'dd/MM/yyyy')
                      }}</span
                    >
                  </td>
                  <td>
                    <p-tag
                      [value]="reg.cdestado"
                      [severity]="getSeveridad(reg.cdestado)"
                      [pTooltip]="reg.cdestado"
                    />
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(reg.dserrorInsert)"
                      >{{ textoTabla(reg.dserrorInsert) }}</span
                    >
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          @if (totalRegistros() > 0) {
            <app-busqueda-paginador
              [currentPage]="currentPage()"
              [totalPages]="totalPages()"
              [hasPrevious]="hasPrevious()"
              [hasNext]="hasNext()"
              [pageSizeOptions]="pageSizeOptions"
              [selectedPageSize]="pageSize()"
              (pageSizeChange)="onPageSizeChange($event)"
              (first)="irPrimeraPagina()"
              (previous)="irPaginaAnterior()"
              (next)="irPaginaSiguiente()"
              (last)="irUltimaPagina()"
            ></app-busqueda-paginador>
          }
        </p-card>
      }

      <!-- Sin resultados -->
      @if (!cargando() && consultaRealizada() && resultados().length === 0) {
        <p-messages
          [value]="[
            {
              severity: 'warn',
              summary: 'Sin resultados',
              detail: 'La busqueda no arrojo resultados.'
            }
          ]"
          [closable]="false"
        />
      }
    </div>
  `
})
export class ResultadoCargaMasivaComponent {
  private readonly cargaMasivaBaseUrl = `${environment.backend.baseUrl}/servicios/cumplimiento/carga-masiva`;
  private readonly http = inject(HttpClient);

  async exportarExcel(): Promise<void> {
    // Validaciones igual que consultar()
    if (!this.fechaInicial) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'Debe ingresar el rango de fecha.'
      });
      return;
    }
    if (!this.fechaFinal) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'Debe ingresar el rango de fecha.'
      });
      return;
    }
    const estados = this.getEstadosSeleccionados();
    if (estados.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'Debe seleccionar al menos un estado de carga.'
      });
      return;
    }
    if (this.fechaInicial > this.fechaFinal) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'La fecha inicial debe ser menor o igual a la fecha final.'
      });
      return;
    }

    this.cargando.set(true);
    try {
      // Construir query params
      const params = new URLSearchParams();
      params.append(
        'fechaInicial',
        this.fechaInicial.toISOString().slice(0, 10)
      );
      params.append('fechaFinal', this.fechaFinal.toISOString().slice(0, 10));
      estados.forEach((e) => params.append('estados', e));

      // 1. Consultar si hay registros usando /resultado
      const resultadoUrl = `${this.cargaMasivaBaseUrl}/resultado?${params.toString()}`;
      const registros = await firstValueFrom(
        this.http.get<CargaCumplimiento[]>(resultadoUrl)
      );
      if (!Array.isArray(registros) || registros.length === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'Sin resultados',
          detail:
            'No se encontraron resultados de cargas realizadas en las fechas buscadas.'
        });
        return;
      }

      // 2. Si hay registros, descargar el Excel
      const url = `${this.cargaMasivaBaseUrl}/descargar?${params.toString()}`;
      const blob = await firstValueFrom(
        this.http.get(url, { responseType: 'blob' })
      );
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'Reporte Carga Cumplimientos sin OC.xls';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ocurrio un error al exportar los resultados.'
      });
    } finally {
      this.cargando.set(false);
    }
  }

  private readonly repository = inject(CargaCumplimientoRepository);
  private readonly messageService = inject(MessageService);

  // Estado reactivo - Angular 19 Signals
  fechaInicial: Date | null = null;
  fechaFinal: Date | null = null;
  pendiente = false;
  procesado = false;
  error = false;

  readonly today = new Date();
  readonly cargando = signal(false);
  readonly resultados = signal<CargaCumplimiento[]>([]);
  readonly consultaRealizada = signal(false);

  // Paginación manual (frontend-girop §17 / ERROR 6 - prohibido [paginator]="true")
  readonly currentPage = signal(1);
  readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  readonly totalRegistros = computed(() => this.resultados().length);
  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalRegistros() / this.pageSize()))
  );
  readonly paginatedResultados = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.resultados().slice(start, start + this.pageSize());
  });
  readonly registroInicio = computed(() =>
    this.totalRegistros() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1
  );
  readonly registroFin = computed(() =>
    Math.min(this.currentPage() * this.pageSize(), this.totalRegistros())
  );
  readonly hasPrevious = computed(() => this.currentPage() > 1);
  readonly hasNext = computed(() => this.currentPage() < this.totalPages());

  irPrimeraPagina(): void {
    this.currentPage.set(1);
  }
  irUltimaPagina(): void {
    this.currentPage.set(this.totalPages());
  }
  irPaginaAnterior(): void {
    if (this.hasPrevious()) this.currentPage.update((p) => p - 1);
  }
  irPaginaSiguiente(): void {
    if (this.hasNext()) this.currentPage.update((p) => p + 1);
  }
  onPageSizeChange(nuevoTamanio: number): void {
    this.pageSize.set(nuevoTamanio);
    this.currentPage.set(1);
  }
  readonly tableConfig = computed(() =>
    massiveResultPreset.createConfig({
      data: this.resultados(),
      pagination: {
        enabled: true,
        mode: 'client',
        pageSize: 10,
        pageSizeOptions: [10, 25, 50],
        currentPageReportTemplate:
          'Mostrando {first} a {last} de {totalRecords} registros'
      },
      style: {
        tableClassName: 'tabla-resultados',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.cargando(),
        emptyMessage: 'No se encontraron resultados'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '320px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de resultados de carga masiva',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  /**
   * Ejecuta la consulta con validaciones del legacy.
   * Legacy: CumplimientoSinOcViewImpl.validarCampos() + generadorReportesExcel()
   */
  async consultar(): Promise<void> {
    // Validaciones - Legacy: CumplimientoSinOcViewImpl.validarCampos()
    if (!this.fechaInicial) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'Debe ingresar el rango de fecha.'
      });
      return;
    }
    if (!this.fechaFinal) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'Debe ingresar el rango de fecha.'
      });
      return;
    }
    const estados = this.getEstadosSeleccionados();
    if (estados.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'Debe seleccionar al menos un estado de carga.'
      });
      return;
    }
    if (this.fechaInicial > this.fechaFinal) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'La fecha inicial debe ser menor o igual a la fecha final.'
      });
      return;
    }

    this.cargando.set(true);
    this.consultaRealizada.set(false);
    this.resultados.set([]);

    try {
      const data = await this.repository.consultarResultado({
        fechaInicial: this.fechaInicial,
        fechaFinal: this.fechaFinal,
        estados
      });
      this.resultados.set(data);
      this.currentPage.set(1);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'Ocurrio un error al consultar los resultados. Intente nuevamente.'
      });
    } finally {
      this.cargando.set(false);
      this.consultaRealizada.set(true);
    }
  }

  limpiar(): void {
    this.fechaInicial = null;
    this.fechaFinal = null;
    this.pendiente = false;
    this.procesado = false;
    this.error = false;
    this.resultados.set([]);
    this.consultaRealizada.set(false);
    this.currentPage.set(1);
  }

  /** Mapea checkboxes a lista de estados. Legacy: CumplimientoSinOcViewImpl.getEstadosSeleccionados() */
  private getEstadosSeleccionados(): EstadoCarga[] {
    const estados: EstadoCarga[] = [];
    if (this.pendiente) estados.push('PENDIENTE');
    if (this.procesado) estados.push('PROCESADO');
    if (this.error) estados.push('ERROR');
    return estados;
  }

  getSeveridad(estado: EstadoCarga): 'success' | 'warn' | 'danger' | 'info' {
    switch (estado) {
      case 'PROCESADO':
        return 'success';
      case 'PENDIENTE':
        return 'warn';
      case 'ERROR':
        return 'danger';
      default:
        return 'info';
    }
  }
}
